import { useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import TradingChart from "../../../../../Components/PerpsComponents/TradingChart";
import TradingForm from "../../../../../Components/PerpsComponents/TradingForm";
import TradingTable from "../../../../../Components/PerpsComponents/TradingTable";
import { TradingTopBar } from "../../../../../Components/PerpsComponents/TradingTopBar";
import TrendingMarquee from "../../../../../Components/PerpsComponents/TrendingMarquee";
import { getMarketById, formatMarketIndex } from "../../../../../config/markets";
import AssetNews from "../../../../../Components/PerpsComponents/AssetNews";
import useIsMobile from "../../../../../hooks/useIsMobile";

/**
 * Watch Trading Component
 * Displays the full trading interface for watch markets (GroupIndex 6)
 * Mirrors the RealEstateTrading layout with TradingForm, TradingTable, TradingTopBar
 */

// Map URL IDs to config IDs
const normalizeMarketId = (id) => {
  const idMap = {
    'patekphilippe': 'patek-philippe',
    'audemarspiguet': 'audemars-piguet',
    'watchindex': 'watch-index',
  };
  return idMap[id] || id;
};

const WatchTrading = () => {
  const { marketId } = useParams();
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();

  // Refs for synced background scroll
  const containerRef = useRef(null);
  const leftPanelRef = useRef(null);
  const rightPanelRef = useRef(null);

  // Get highlight trade index from URL query params
  const highlightTradeIndex = searchParams.get('highlightTrade');
  const autoOpenDetail = searchParams.get('openDetail') === 'true';

  const normalizedMarketId = normalizeMarketId(marketId);
  const market = getMarketById(normalizedMarketId);

  // When scrolling on background (not on a panel), scroll both panels together
  const handleContainerWheel = useCallback((e) => {
    const leftPanel = leftPanelRef.current;
    const rightPanel = rightPanelRef.current;
    if (!leftPanel || !rightPanel) return;

    const target = e.target;
    if (leftPanel.contains(target) || rightPanel.contains(target)) {
      return;
    }

    e.preventDefault();
    leftPanel.scrollTop += e.deltaY;
    rightPanel.scrollTop += e.deltaY;
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener('wheel', handleContainerWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleContainerWheel);
  }, [handleContainerWheel]);

  if (!market) {
    return (
      <div className="w-full min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Market Not Found</h2>
          <p className="text-gray-600">The market "{marketId}" does not exist.</p>
        </div>
      </div>
    );
  }

  const marketIndex = formatMarketIndex(market.marketIndex);

  // Mobile layout
  if (isMobile) {
    return (
      <div className="w-full min-h-screen bg-gray-50" style={{ paddingBottom: "var(--app-marquee-h, 36px)" }}>
        <TrendingMarquee />
        <TradingTopBar tokenIndex={market.tokenIndex} />
        <div className="px-4 py-2">
          <div className="relative">
            <TradingChart chartType={market.chartType} />
          </div>
        </div>
        <div className="mobile-trading-form">
          <TradingForm
            tokenIndex={market.tokenIndex}
            marketIndex={marketIndex}
          />
        </div>
        <div className="bg-white mb-5">
          <TradingTable market={marketIndex} highlightTradeIndex={highlightTradeIndex} autoOpenDetail={autoOpenDetail} />
        </div>
        <AssetNews marketId={market.id} />
      </div>
    );
  }

  // Desktop layout
  return (
    <div ref={containerRef} className="w-full overflow-hidden"
      /* Fills the viewport below the header exactly. This was
         `calc(100vh - 100px)` against a 68px header, which left a 32px
         dead strip at the bottom of every trading page. */
      style={{ height: "calc(100vh - var(--app-header-h, 68px) - var(--app-marquee-h, 36px))" }}>
      <div className="flex gap-5 w-[92%] max-w-[1600px] mx-auto h-full pt-5">
        <div ref={leftPanelRef} className="flex-1 min-w-0 overflow-y-auto pr-1 trading-panel-scroll">
          <TrendingMarquee />
          <TradingTopBar tokenIndex={market.tokenIndex} />
          <div className="relative">
            <TradingChart chartType={market.chartType} />
          </div>
          <TradingTable market={marketIndex} highlightTradeIndex={highlightTradeIndex} autoOpenDetail={autoOpenDetail} />
          <AssetNews marketId={market.id} />
        </div>
        {/* The order rail scrolls as one column: the ticket keeps its natural
            height and the submit button sits at the bottom of it, reached by
            scrolling this rail. Pinning the button and scrolling only the
            ticket's middle was tried and reverted — a CTA that never moves
            reads as detached from the form it submits. */}
        <div ref={rightPanelRef} className="w-[340px] xl:w-[400px] flex-shrink-0 overflow-y-auto pl-1 pb-5 trading-panel-scroll">
          <TradingForm
            tokenIndex={market.tokenIndex}
            marketIndex={marketIndex}
          />
        </div>
      </div>
    </div>
  );
};

export default WatchTrading;
